"use client";

import { RenderProviders } from "@/components/presentational/tailwind/Auth/RenderProviders";
import { PasskeyButton } from "@/components/presentational/tailwind/Auth/PasskeyButton";
import { Email } from "@/components/presentational/tailwind/Auth/Email";
import { EmailAndPassword } from "@/components/presentational/tailwind/Auth/EmailAndPassword";
import { useSignInWithMagicLink, useSignInWithPassword, useSignInWithProvider } from "@/utils/react-query-hooks";
import React, { useState } from "react";
import { T } from "gt-next";
import { APP_DOMAIN, ROOT_DOMAIN } from "@/constants";
import Anchor from "@shared-ui/components/Anchor";
import { Spinner } from "@/components/ui/spinner";
export function Login() {
  const [isSuccessful, setIsSuccessful] = useState(false);
  function redirectToDashboard() {
    setIsSuccessful(true);
    const protocol = process.env.NODE_ENV == "production" ? "https://" : "http://";
    // Honor a same-origin returnTo (e.g. the /update-payment decline-recovery link).
    // Guard against open-redirect: accept only bare app-relative paths ("/x"),
    // never "//host", a scheme, or a backslash. No param → unchanged dashboard redirect.
    let returnTo: string | null = null;
    try {
      const raw = new URLSearchParams(window.location.search).get("returnTo");
      if (raw && raw.startsWith("/") && !raw.startsWith("//") && !raw.includes("://") && !raw.includes("\\")) {
        returnTo = raw;
      }
    } catch {
      // ignore malformed search string — fall back to dashboard
    }
    window.location.href = returnTo ? `${protocol}${ROOT_DOMAIN}${returnTo}` : `${protocol}${APP_DOMAIN}`;
  }
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showMagicLink, setShowMagicLink] = useState<boolean>(false);

  // Google Login
  const providerMutation = useSignInWithProvider();
  // Email and Password Login
  const passwordMutation = useSignInWithPassword({
    onSuccess: redirectToDashboard,
    onError: (error: any) => {
      setErrorMessage(error.message);
    }
  });
  // Magic Link Login
  const magicLinkMutation = useSignInWithMagicLink({
    onSuccess: () => {
      setSuccessMessage("A magic link has been sent to your email!");
    },
    onError: (error_0: any) => {
      setErrorMessage(error_0.message);
    }
  });
  return <div className="w-full">
            {isSuccessful ? <div className="flex flex-col items-center justify-center py-12">
                    <Spinner size="xl" />
                    <p className="mt-4 text-[15px] font-medium text-gray-600"><T>Logging you in...</T></p>
                </div> : <div className="space-y-4 lg:space-y-6">
                    {/* Header - centered on mobile, left-aligned on desktop */}
                    <div className="space-y-1 text-center lg:space-y-2 lg:text-left">
                        <h1 className="text-[28px] font-bold text-gray-900 sm:text-3xl"><T>Welcome back</T></h1>
                        <p className="text-[15px] text-gray-500"><T>Log in to your Outfame account</T></p>
                    </div>

                    {/* Error message */}
                    {errorMessage && <div className="relative rounded-xl bg-red-500 px-4 py-3 text-white" role="alert">
                            <span className="block sm:inline">{errorMessage}</span>
                            <button className="absolute right-3 top-3 text-white/80 hover:text-white" onClick={() => setErrorMessage(null)}>
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>}

                    {showMagicLink ? <Email successMessage={successMessage} isLoading={magicLinkMutation.isLoading} view="sign-in" onSubmit={email => {
        magicLinkMutation.mutate({
          email
        });
      }} /> : <>
                            {/* Auth buttons - grouped together */}
                            <div className="space-y-2.5">
                                <RenderProviders providers={["google", "facebook"]} isLoading={providerMutation.isLoading} onProviderLoginRequested={provider => {
            providerMutation.mutate({
              provider
            });
          }} isLogin={true} />
                                {/* Passkey Login */}
                                <PasskeyButton onSuccess={() => redirectToDashboard()} isLogin={true} />
                            </div>

                            {/* OR Divider */}
                            <div className="relative py-1">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="bg-white px-4 text-[13px] text-gray-400"><T>OR</T></span>
                                </div>
                            </div>

                            {/* Email/Password form */}
                            <EmailAndPassword isLoading={passwordMutation.isLoading} onSubmit={data => {
          passwordMutation.mutate(data);
        }} view="sign-in" />
                        </>}

                    {/* Magic link toggle */}
                    <p className="text-center text-[14px] text-gray-500">
                        <T>Or log in</T>{" "}
                        {!showMagicLink ? <button className="focus:outline-hidden font-medium text-gray-900 hover:underline" onClick={() => setShowMagicLink(true)}>
                                <T>using a magic link →</T>
                            </button> : <button className="focus:outline-hidden font-medium text-gray-900 hover:underline" onClick={() => setShowMagicLink(false)}>
                                <T>using email and password →</T>
                            </button>}
                    </p>

                    {/* Divider */}
                    <hr className="border-gray-200" />

                    {/* Sign up link */}
                    <p className="text-center text-[14px] text-gray-500">
                        <T>Don't have an account?{" "}
                        <Anchor href="/sign-up" title="Sign Up" className="font-medium text-gray-900 hover:underline">
                            Sign up
                        </Anchor></T>
                    </p>
                </div>}
        </div>;
}